import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AsyncActionButton from "../components/AsyncActionButton";
import { useLanguage } from "../i18n/LanguageContext";
import ExpandableText from "../components/ExpandableText";
import ReminderPanel, { ReminderWarning, reminderPayloadKontrolEt } from "../components/ReminderPanel";
import ScanPreviewModal from "../components/ScanPreviewModal";
import { fotografSec, metinTani, alisverisListesiParse } from "../services/mlkitService";
import { urunleriTopluEkleService } from "../services/shoppingService";
import alisverisAsset from "../assets/alisverisasset.png";

function ShoppingIcon({ name, size = 18 }) {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
    focusable: "false",
  };

  if (name === "camera") {
    return <svg {...common}><path d="M14.5 4 16 7h3a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h3l1.5-3h5Z"/><circle cx="12" cy="13" r="3.5"/></svg>;
  }
  if (name === "gallery") {
    return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="9" r="1.5"/><path d="m21 15-4.5-4.5L7 20"/></svg>;
  }
  if (name === "sliders") {
    return <svg {...common}><path d="M4 7h10M18 7h2M4 17h2M10 17h10"/><circle cx="16" cy="7" r="2"/><circle cx="8" cy="17" r="2"/></svg>;
  }
  if (name === "chevronUp") {
    return <svg {...common}><path d="m6 15 6-6 6 6"/></svg>;
  }
  if (name === "chevronDown") {
    return <svg {...common}><path d="m6 9 6 6 6-6"/></svg>;
  }
  if (name === "check") {
    return <svg {...common}><path d="m5 12 4 4L19 6"/></svg>;
  }
  return <svg {...common}><path d="M12 5v14M5 12h14"/></svg>;
}

function ShoppingPage({
  alisverisFormAcik, setAlisverisFormAcik,
  toplamUrun, bekleyenUrun, alinanUrun,
  alisverisKategorileri, seciliKategori, setSeciliKategori,
  urunAdi, setUrunAdi, urunMiktar, setUrunMiktar,
  urunBirim, setUrunBirim, urunKategori, setUrunKategori,
  urunEkle, filtreliUrunler, alindiDegistir, urunSil,
  permissions, user,
  familyId, tamAd, profilAvatar,
  onReminderKur,
}) {
  const { t, tList, lang } = useLanguage();
  const birimler = tList("const.units");
  const quickInputRef = useRef(null);
  const categoryRailRef = useRef(null);
  const categoryScrollTargetRef = useRef(null);
  const ekleniyorRef = useRef(false);

  const [scanAcik, setScanAcik] = useState(false);
  const [scanYukleni, setScanYukleni] = useState(false);
  const [scanHata, setScanHata] = useState("");
  const [scanUrunler, setScanUrunler] = useState([]);
  const [scanEklendi, setScanEklendi] = useState(false);
  const [scanIlerleme, setScanIlerleme] = useState(0);
  const [scanSecenekAcik, setScanSecenekAcik] = useState(false);
  const [ekleniyor, setEkleniyor] = useState(false);
  const [alinanlarAcik, setAlinanlarAcik] = useState(true);
  const [siliniyorId, setSiliniyorId] = useState(null);
  const [categoryScrollState, setCategoryScrollState] = useState({ left: false, right: false });

  const [reminderAktif, setReminderAktif] = useState(false);
  const [reminderTarih, setReminderTarih] = useState("");
  const [reminderSaat, setReminderSaat] = useState("09:00");
  const [reminderTekrar, setReminderTekrar] = useState("none");
  const [reminderError, setReminderError] = useState("");

  const shoppingCategoryLabels = useMemo(() => {
    const tr = [
      "Tümü", "Market", "Manav", "Kasap", "Şarküteri", "Fırın",
      "Temizlik", "Mutfak", "İçecek", "Kahvaltılık", "Atıştırmalık",
      "Kişisel Bakım", "Bebek", "Evcil Hayvan", "Elektronik",
      "Eczane", "Giyim", "Ev İhtiyaçları", "Diğer",
    ];
    const en = [
      "All", "Grocery", "Greengrocer", "Butcher", "Deli", "Bakery",
      "Cleaning", "Kitchen", "Beverages", "Breakfast", "Snacks",
      "Personal Care", "Baby", "Pet Supplies", "Electronics",
      "Pharmacy", "Clothing", "Home Supplies", "Other",
    ];
    return tr.reduce((acc, item, index) => {
      acc[item] = lang === "en" ? en[index] : item;
      acc[en[index]] = lang === "en" ? en[index] : item;
      return acc;
    }, {});
  }, [lang]);

  const unitLabels = useMemo(() => {
    const tr = ["adet", "kg", "lt", "paket"];
    const en = ["pcs", "kg", "lt", "pack"];
    return tr.reduce((acc, item, index) => {
      acc[item] = lang === "en" ? en[index] : item;
      acc[en[index]] = lang === "en" ? en[index] : item;
      return acc;
    }, {});
  }, [lang]);

  const kategoriGoster = useCallback(
    (value) => shoppingCategoryLabels[value] || value || t("shopping.noCategory"),
    [shoppingCategoryLabels, t]
  );
  const birimGoster = useCallback(
    (value) => unitLabels[value] || value || "",
    [unitLabels]
  );

  const canAdd = permissions?.actions?.addShopping;
  const canDeleteAll = permissions?.actions?.deleteAllShopping;
  const canDeleteOwn = permissions?.actions?.deleteOwnShopping;
  const urunEklemeyeHazir = urunAdi.trim().length > 0 && Number(urunMiktar) > 0;

  const handleScan = useCallback(async (kaynak) => {
    setScanSecenekAcik(false);
    setScanHata("");
    setScanEklendi(false);
    setScanIlerleme(0);
    setScanYukleni(true);
    try {
      const { dataUrl } = await fotografSec(kaynak);
      const rawText = await metinTani(dataUrl, (pct) => setScanIlerleme(pct), { mode: "shopping" });
      const parsed = alisverisListesiParse(rawText, {
        availableCategories: alisverisKategorileri,
        availableUnits: birimler,
      });
      if (!parsed.length) {
        setScanHata(t("shopping.scan.noItems"));
        return;
      }
      setScanUrunler(parsed);
      setScanAcik(true);
    } catch (err) {
      if (err?.message === "CANCELLED") return;
      setScanHata(err?.message === "NATIVE_OCR_REQUIRED"
        ? t("shopping.scan.nativeOnly")
        : t("shopping.scan.failed"));
    } finally {
      setScanYukleni(false);
      setScanIlerleme(0);
    }
  }, [alisverisKategorileri, birimler, t]);

  const handleScanConfirm = useCallback(async (urunler) => {
    if (!familyId || !user) return false;
    const result = await urunleriTopluEkleService({
      familyId,
      urunler,
      urunAdlari: urunler,
      urunKategori: alisverisKategorileri?.find((k) => k !== "Tümü" && k !== "All") || "Market",
      tamAd,
      user,
      profilAvatar,
    });
    if ((result?.count || 0) > 0) {
      setScanAcik(false);
      setScanEklendi(true);
      window.setTimeout(() => setScanEklendi(false), 3000);
      return true;
    }
    return false;
  }, [familyId, user, tamAd, profilAvatar, alisverisKategorileri]);

  const handleUrunEkle = useCallback(async () => {
    if (ekleniyorRef.current || !urunAdi.trim() || Number(urunMiktar) <= 0) return;

    const urunAdiSnapshot = urunAdi.trim();
    const birimSnapshot = urunBirim;
    const kategoriSnapshot = urunKategori;
    let reminderPayload = null;

    if (reminderAktif && onReminderKur) {
      reminderPayload = reminderPayloadKontrolEt({
        mod: "datetime",
        tarih: reminderTarih,
        saat: reminderSaat,
        tekrar: reminderTekrar,
      }, t, lang, setReminderError);
      if (!reminderPayload) return;
    }

    ekleniyorRef.current = true;
    setEkleniyor(true);
    try {
      const yeniUrunId = await urunEkle();
      if (!yeniUrunId) return false;

      // App içindeki kayıt fonksiyonu birimi kg'ye sıfırlıyor. Hızlı eklemede
      // kullanıcının son seçimi korunur; kategori davranışı da değişmez.
      setUrunBirim(birimSnapshot);
      setUrunKategori(kategoriSnapshot);
      setAlisverisFormAcik(false);

      if (reminderPayload && onReminderKur) {
        await onReminderKur({
          baslik: t("shopping.reminderTitle"),
          aciklama: t("notify.shoppingReminder.item", { item: urunAdiSnapshot }),
          baslikKey: "shopping.reminderTitle",
          aciklamaKey: "notify.shoppingReminder.item",
          aciklamaVars: { item: urunAdiSnapshot },
          kaynak: "shopping",
          kaynakId: yeniUrunId,
          kaynakTuru: "shoppingItem",
          url: "/?pushPage=alisveris",
          ...reminderPayload,
        }).catch(() => {});
        setReminderAktif(false);
        setReminderTarih("");
        setReminderSaat("09:00");
        setReminderTekrar("none");
        setReminderError("");
      }
      window.setTimeout(() => quickInputRef.current?.focus(), 80);
      return true;
    } finally {
      ekleniyorRef.current = false;
      setEkleniyor(false);
    }
  }, [
    urunAdi, urunMiktar, urunBirim, urunKategori,
    reminderAktif, reminderTarih, reminderSaat, reminderTekrar,
    onReminderKur, t, lang, urunEkle, setUrunBirim, setUrunKategori,
    setAlisverisFormAcik,
  ]);

  const tarihMs = (value) => {
    if (!value) return 0;
    if (typeof value?.toMillis === "function") return value.toMillis();
    if (typeof value?.seconds === "number") return value.seconds * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const { bekleyenler, alinanlar } = useMemo(() => {
    const sirali = [...filtreliUrunler].sort((a, b) => {
      const aTarih = tarihMs(a.alindi ? (a.alindiTarihi || a.eklenmeTarihi) : a.eklenmeTarihi);
      const bTarih = tarihMs(b.alindi ? (b.alindiTarihi || b.eklenmeTarihi) : b.eklenmeTarihi);
      return bTarih - aTarih;
    });
    return {
      bekleyenler: sirali.filter((urun) => !urun.alindi),
      alinanlar: sirali.filter((urun) => !!urun.alindi),
    };
  }, [filtreliUrunler]);

  const ilerlemeYuzdesi = toplamUrun > 0
    ? Math.max(0, Math.min(100, Math.round((alinanUrun / toplamUrun) * 100)))
    : 0;
  const allCategory = seciliKategori === "Tümü" || seciliKategori === "All" || seciliKategori === tList("const.shoppingCategories")[0];

  const progressMessage = toplamUrun === 0
    ? t("shopping.progress.start")
    : bekleyenUrun === 0
      ? t(alinanUrun === 1 ? "shopping.progress.doneOne" : "shopping.progress.done", { count: alinanUrun })
      : t(bekleyenUrun === 1 ? "shopping.progress.pendingOne" : "shopping.progress.pending", { count: bekleyenUrun });

  const updateCategoryScrollState = useCallback(() => {
    const rail = categoryRailRef.current;
    if (!rail) return;
    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const normalizedScrollLeft = Math.max(0, Math.min(maxScrollLeft, rail.scrollLeft));
    if (categoryScrollTargetRef.current !== null
      && Math.abs(normalizedScrollLeft - categoryScrollTargetRef.current) <= 2) {
      categoryScrollTargetRef.current = null;
    }
    setCategoryScrollState({
      left: normalizedScrollLeft > 3,
      right: normalizedScrollLeft < maxScrollLeft - 3,
    });
  }, []);

  useEffect(() => {
    const rail = categoryRailRef.current;
    if (!rail) return undefined;

    updateCategoryScrollState();
    const observer = typeof ResizeObserver === "function"
      ? new ResizeObserver(updateCategoryScrollState)
      : null;
    observer?.observe(rail);
    window.addEventListener("resize", updateCategoryScrollState);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateCategoryScrollState);
    };
  }, [alisverisKategorileri, updateCategoryScrollState]);

  const scrollCategories = (direction) => {
    const rail = categoryRailRef.current;
    if (!rail) return;
    const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const currentTarget = categoryScrollTargetRef.current ?? rail.scrollLeft;
    const nextTarget = Math.max(0, Math.min(
      maxScrollLeft,
      currentTarget + direction * Math.max(120, rail.clientWidth * 0.58)
    ));
    categoryScrollTargetRef.current = nextTarget;
    rail.scrollTo({
      left: nextTarget,
      behavior: "smooth",
    });
  };

  const handleKategoriSec = (kategori, button) => {
    setSeciliKategori(kategori);
    const isAll = kategori === "Tümü" || kategori === "All" || kategori === tList("const.shoppingCategories")[0];
    if (!isAll) setUrunKategori(kategori);
    categoryScrollTargetRef.current = null;
    button?.scrollIntoView?.({ behavior: "smooth", block: "nearest", inline: "nearest" });
  };

  const handleSil = async (urun) => {
    if (siliniyorId) return;
    setSiliniyorId(urun.id);
    try {
      await urunSil(urun);
    } finally {
      setSiliniyorId(null);
    }
  };

  const renderProductRow = (urun, index, total) => {
    const canDelete = canDeleteAll || (canDeleteOwn && urun.ekleyenUid === user?.uid);
    return (
      <div
        key={urun.id}
        className={`tableRow shoppingListRow ${urun.alindi ? "tableRowDone" : ""} ${index === total - 1 ? "tableRowLast" : ""}`}
      >
        <button
          type="button"
          onClick={() => alindiDegistir(urun)}
          className={urun.alindi ? "checkButton checked" : "checkButton"}
          aria-label={urun.alindi ? t("shopping.markPending") : t("shopping.markTaken")}
        >
          {urun.alindi && <ShoppingIcon name="check" size={17} />}
        </button>
        <div className="tableRowContent shoppingRowContent">
          <ExpandableText
            text={urun.ad}
            maxLength={34}
            className={urun.alindi ? "tableRowTitle strike shoppingExpandableTitle" : "tableRowTitle shoppingExpandableTitle"}
          />
          <ExpandableText
            text={`${urun.miktar || ""} ${birimGoster(urun.birim)} · ${kategoriGoster(urun.kategori)} · ${urun.ekleyen || t("shopping.noAdder")}`}
            maxLength={42}
            className="tableRowSub shoppingExpandableSub"
          />
        </div>
        {canDelete && (
          <button
            type="button"
            className="rowDeleteBtn"
            disabled={siliniyorId === urun.id}
            aria-label={siliniyorId === urun.id ? t("shopping.deleting") : t("common.delete")}
            onClick={() => handleSil(urun)}
          >
            {siliniyorId === urun.id
              ? <span className="shoppingButtonSpinner" aria-hidden="true" />
              : t("common.delete")}
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="pageContent shoppingProPage">
      <section className="shoppingProgressCard" aria-label={t("shopping.listTitle")}>
        <div className="shoppingProgressTop">
          <div>
            <h2>{t("shopping.listTitle")}</h2>
            <p>{progressMessage}</p>
          </div>
          <strong>{ilerlemeYuzdesi}%</strong>
        </div>
        <div
          className="shoppingProgressTrack"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={ilerlemeYuzdesi}
        >
          <span style={{ width: `${ilerlemeYuzdesi}%` }} />
        </div>
        <div className="shoppingProgressStats">
          <span><b>{toplamUrun}</b> {t("common.total")}</span>
          <span className="pending"><b>{bekleyenUrun}</b> {t("common.pending")}</span>
          <span className="taken"><b>{alinanUrun}</b> {t("common.taken")}</span>
        </div>
      </section>

      <div className={`shoppingCategoryRail ${categoryScrollState.left ? "canScrollLeft" : ""} ${categoryScrollState.right ? "canScrollRight" : ""}`}>
        {categoryScrollState.left && (
          <button type="button" className="shoppingCategoryNav previous" onClick={() => scrollCategories(-1)} aria-label={t("shopping.categoriesPrevious")}>
            <ShoppingIcon name="chevronUp" size={16} />
          </button>
        )}
        <div
          ref={categoryRailRef}
          className="categoryChips noSwipeBack"
          data-no-swipe-back="true"
          aria-label={t("shopping.categories")}
          onScroll={updateCategoryScrollState}
          onPointerDown={() => { categoryScrollTargetRef.current = null; }}
          onTouchStart={() => { categoryScrollTargetRef.current = null; }}
        >
          {alisverisKategorileri.map((kategori) => (
            <button
              key={kategori}
              type="button"
              className={seciliKategori === kategori ? "chip activeBlue" : "chip"}
              onClick={(event) => handleKategoriSec(kategori, event.currentTarget)}
            >
              {kategoriGoster(kategori)}
            </button>
          ))}
        </div>
        {categoryScrollState.right && (
          <button type="button" className="shoppingCategoryNav next" onClick={() => scrollCategories(1)} aria-label={t("shopping.categoriesNext")}>
            <ShoppingIcon name="chevronDown" size={16} />
          </button>
        )}
      </div>

      {canAdd && (
        <section className={`shoppingQuickCard ${alisverisFormAcik ? "expanded" : ""}`}>
          <form className="shoppingQuickComposer" onSubmit={(event) => event.preventDefault()}>
            <div className="shoppingQuickInputWrap">
              <ShoppingIcon name="plus" size={18} />
              <input
                ref={quickInputRef}
                type="text"
                value={urunAdi}
                onChange={(event) => setUrunAdi(event.target.value)}
                placeholder={t("shopping.quickPlaceholder")}
                aria-label={t("shopping.itemName")}
                autoComplete="off"
                enterKeyHint="done"
              />
            </div>
            <button
              type="button"
              className={`shoppingQuickIconButton ${scanSecenekAcik ? "active" : ""}`}
              onClick={() => setScanSecenekAcik((open) => !open)}
              aria-label={t("shopping.scan.open")}
              aria-expanded={scanSecenekAcik}
              disabled={scanYukleni}
            >
              <ShoppingIcon name="camera" size={19} />
            </button>
            <AsyncActionButton
              type="submit"
              className="shoppingQuickAddButton"
              onAction={handleUrunEkle}
              externalBusy={ekleniyor}
              compact
              aria-label={t("shopping.addToList")}
              disabled={ekleniyor || !urunEklemeyeHazir}
            >
              <ShoppingIcon name="plus" size={20} />
            </AsyncActionButton>
          </form>

          <div className="shoppingQuickMeta">
            <button type="button" onClick={() => setAlisverisFormAcik(true)}>
              <span>{urunMiktar || "1"} {birimGoster(urunBirim)}</span>
              <i aria-hidden="true">•</i>
              <span>{kategoriGoster(urunKategori)}</span>
            </button>
            <button
              type="button"
              className="shoppingDetailsToggle"
              onClick={() => setAlisverisFormAcik(!alisverisFormAcik)}
              aria-expanded={alisverisFormAcik}
              aria-controls="shopping-advanced-panel"
            >
              <span className="shoppingDetailsIcon"><ShoppingIcon name="sliders" size={17} /></span>
              <span className="shoppingDetailsCopy">
                <strong>{alisverisFormAcik ? t("shopping.hideDetails") : t("shopping.details")}</strong>
                <small>{t("shopping.detailsHint")}</small>
              </span>
              <span className="shoppingDetailsChevron"><ShoppingIcon name={alisverisFormAcik ? "chevronUp" : "chevronDown"} size={16} /></span>
            </button>
          </div>

          {scanSecenekAcik && !scanYukleni && (
            <div className="shoppingScanChoices">
              <button type="button" onClick={() => handleScan("kamera")}>
                <span><ShoppingIcon name="camera" size={18} /></span>
                <b>{t("shopping.scan.camera")}</b>
                <small>{t("shopping.scan.cameraHint")}</small>
              </button>
              <button type="button" onClick={() => handleScan("galeri")}>
                <span><ShoppingIcon name="gallery" size={18} /></span>
                <b>{t("shopping.scan.gallery")}</b>
                <small>{t("shopping.scan.galleryHint")}</small>
              </button>
            </div>
          )}

          {scanYukleni && (
            <div className="shoppingInlineNotice loading" role="status">
              <span className="shoppingButtonSpinner" />
              {scanIlerleme > 0
                ? t("shopping.scan.reading", { percent: scanIlerleme })
                : t("shopping.scan.scanning")}
            </div>
          )}
          {scanHata && <div className="shoppingInlineNotice error" role="alert">{scanHata}</div>}
          {scanEklendi && <div className="shoppingInlineNotice success" role="status"><ShoppingIcon name="check" size={16} /> {t("shopping.scan.success")}</div>}

          {alisverisFormAcik && (
            <div className="shoppingAdvancedPanel" id="shopping-advanced-panel">
              <div className="formRow shoppingAdvancedFields">
                <div className="inputLabelGroup quantity">
                  <label className="darkLabel">{t("common.quantity")}</label>
                  <input className="darkInput" type="number" min="0.01" step="any" inputMode="decimal" placeholder="1" value={urunMiktar} onChange={(event) => setUrunMiktar(event.target.value)} />
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel">{t("common.unit")}</label>
                  <select className="darkInput" value={urunBirim} onChange={(event) => setUrunBirim(event.target.value)}>
                    {birimler.map((birim) => <option key={birim} value={birim}>{birimGoster(birim)}</option>)}
                  </select>
                </div>
                <div className="inputLabelGroup category">
                  <label className="darkLabel">{t("common.category")}</label>
                  <select className="darkInput" value={urunKategori} onChange={(event) => setUrunKategori(event.target.value)}>
                    {alisverisKategorileri
                      .filter((kategori) => kategori !== "Tümü" && kategori !== "All" && kategori !== tList("const.shoppingCategories")[0])
                      .map((kategori) => <option key={kategori} value={kategori}>{kategoriGoster(kategori)}</option>)}
                  </select>
                </div>
              </div>
              <ReminderWarning message={reminderAktif ? reminderError : ""} />
              <ReminderPanel
                aktif={reminderAktif} setAktif={setReminderAktif}
                tarih={reminderTarih} setTarih={setReminderTarih}
                saat={reminderSaat} setSaat={setReminderSaat}
                tekrar={reminderTekrar} setTekrar={setReminderTekrar}
                mod="datetime"
                etiket={t("shopping.reminder")}
              />
              <AsyncActionButton
                type="button"
                className="primaryButton shoppingAdvancedAddButton"
                onAction={handleUrunEkle}
                externalBusy={ekleniyor}
                busyText={t("shopping.adding")}
                disabled={ekleniyor || !urunEklemeyeHazir}
              >
                {t("shopping.addToList")}
              </AsyncActionButton>
            </div>
          )}
        </section>
      )}

      <div className="tableCard shoppingItemsCard">
        {bekleyenler.length === 0 && alinanlar.length === 0 ? (
          <div className="shoppingEmptyPremium" role="status" aria-live="polite">
            <img className="shoppingEmptyPremiumImage" src={alisverisAsset} alt="" aria-hidden="true" loading="eager" decoding="sync" fetchPriority="high" draggable="false" />
            <h3 className="shoppingEmptyPremiumTitle">
              {toplamUrun > 0 ? t("shopping.emptyCategoryTitle") : t("shopping.emptyTitle")}
            </h3>
            <p className="shoppingEmptyPremiumText">
              {toplamUrun > 0 ? t("shopping.emptyCategoryText") : t("shopping.emptyText")}
            </p>
            {canAdd && toplamUrun === 0 && (
              <button type="button" className="shoppingEmptyAddButton" onClick={() => quickInputRef.current?.focus()}>
                <ShoppingIcon name="plus" size={17} /> {t("shopping.startAdding")}
              </button>
            )}
          </div>
        ) : (
          <>
            {bekleyenler.length > 0 && (
              <section className="shoppingListSection">
                <div className="shoppingSectionHeader">
                  <span className="shoppingSectionDot pending" />
                  <h3>{t("shopping.pendingSection")}</h3>
                  <b>{bekleyenler.length}</b>
                </div>
                {bekleyenler.map((urun, index) => renderProductRow(urun, index, bekleyenler.length))}
              </section>
            )}

            {bekleyenler.length === 0 && alinanlar.length > 0 && (
              <div className="shoppingCompleteBanner" role="status">
                <span><ShoppingIcon name="check" size={20} /></span>
                <div>
                  <h3>{allCategory ? t("shopping.completeTitle") : t("shopping.categoryCompleteTitle")}</h3>
                  <p>{allCategory ? t("shopping.completeText") : t("shopping.categoryCompleteText")}</p>
                </div>
              </div>
            )}

            {alinanlar.length > 0 && (
              <section className="shoppingListSection completed">
                <button type="button" className="shoppingSectionHeader shoppingCompletedToggle" onClick={() => setAlinanlarAcik((open) => !open)} aria-expanded={alinanlarAcik}>
                  <span className="shoppingSectionDot completed" />
                  <h3>{t("shopping.completedSection")}</h3>
                  <b>{alinanlar.length}</b>
                  <span className="shoppingSectionToggleLabel">{alinanlarAcik ? t("shopping.hide") : t("shopping.show")}</span>
                  <ShoppingIcon name={alinanlarAcik ? "chevronUp" : "chevronDown"} size={16} />
                </button>
                {alinanlarAcik && alinanlar.map((urun, index) => renderProductRow(urun, index, alinanlar.length))}
              </section>
            )}
          </>
        )}
      </div>

      {scanAcik && (
        <ScanPreviewModal
          items={scanUrunler}
          categories={alisverisKategorileri}
          units={birimler}
          onConfirm={handleScanConfirm}
          onClose={() => setScanAcik(false)}
        />
      )}
    </div>
  );
}

export default ShoppingPage;
